// Internal modules
import { appStore } from '@/store';
import { ACCOUNT_CREATION_PAYLOAD, logger } from '@/lib/logger';
import { conditionallyDropLoginCookie, getLocale, setAccessToken } from '@/lib';
import { isFunction } from '@/lib/utils/typeUtils';
import { redirectToOpenBanking } from '@/lib/utils/redirects/redirects';

const OB_REDIRECT_TIMEOUT_MS = 10000;

/**
 * @function responseContingencyHandlerFactory
 * @returns {Function} Handler that checks for funding contingencies (e.g. Open Banking)
 *   and payment contingencies (3DS), returning the appropriate result or redirecting.
 */
const responseContingencyHandlerFactory = () => {
  return data => {
    const plan = data?.fundingOptions?.[0]?.allPlans?.[0];
    const obContingency = plan?.fundingContingencies?.find(
      c => c.contingencyType === 'NEED_CONSENT_FOR_BANK_ACCOUNT',
    );

    if (obContingency) {
      const fundingInstrumentId = obContingency.encryptedId;

      logger.info('weasley_open_banking_contingency_detected', {
        fundingInstrumentId,
      });

      logger.fpti('OPEN_BANKING_CONTINGENCY_RECEIVED', {
        funding_instrument_id: fundingInstrumentId || '',
        state_name: 'open_banking',
        transition_name: 'open_banking_contingency_received',
      });

      logger.metric('GUEST_SIGNUP.OPEN_BANKING.CONTINGENCY_RECEIVED', {
        dimensions: {
          has_funding_instrument_id: Boolean(fundingInstrumentId),
        },
      });

      if (fundingInstrumentId) {
        if (!navigator?.cookieEnabled) {
          const error = new Error('OPEN_BANKING_TERMINAL_ERROR');
          error.errorData = { fundingInstrumentId };
          return Promise.reject(error);
        }

        const { ui: uiActions } = appStore.getActions();
        uiActions.toggleIsFormSubmitting(true);

        redirectToOpenBanking(fundingInstrumentId);
        return new Promise((_, reject) => {
          const timeoutId = setTimeout(() => {
            uiActions.toggleIsFormSubmitting(false);
            logger.error('weasley_open_banking_redirect_timeout');
            reject(new Error('WEASLEY_OB_REDIRECT_TIMEOUT'));
          }, OB_REDIRECT_TIMEOUT_MS);

          const cancelTimeout = () => clearTimeout(timeoutId);
          window.addEventListener('beforeunload', cancelTimeout);
          window.addEventListener('pagehide', cancelTimeout);
        });
      }

      logger.error('weasley_open_banking_missing_encrypted_id');
    }

    const { flags, paymentContingencies } = data;
    const {
      threeDomainSecure: threeDS1ContingencyData,
      threeDSContingencyData: threeDS2ContingencyData,
    } = paymentContingencies || {};

    if (flags?.is3DSecureRequired) {
      // if 3DS 1.0 and 2.0 is not success then there was a failure
      // and we need to throw to trigger error handling
      if (
        threeDS1ContingencyData &&
        threeDS1ContingencyData.status !== 'SUCCESS'
      ) {
        logger.error('3ds_1_status_not_success', {
          status: threeDS1ContingencyData.status,
        });
        throw new Error('WEASLEY_3DS_1_ERROR');
      } else if (
        threeDS2ContingencyData &&
        threeDS2ContingencyData.causeName !== 'NEED_3D_SECURE_AUTHENTICATION'
      ) {
        logger.error('3ds_2_status_not_success', {
          causeName: threeDS2ContingencyData.causeName,
        });
        throw new Error('WEASLEY_3DS_2_ERROR');
      } else if (!threeDS1ContingencyData && !threeDS2ContingencyData) {
        // Edge case handling
        // is3DSSecureRequired is set to true but contingency data is missing
        logger.error('3ds_contingency_data_missing');
        throw new Error('WEASLEY_3DS_ERROR');
      }
    }

    return {
      is3DSecureRequired: Boolean(flags?.is3DSecureRequired),
      threeDS1ContingencyData,
      threeDS2ContingencyData,
    };
  };
};

/**
 * @function responseHandlerFactory
 * @param {object} state
 * @returns {object} factory function which
 * returns response data from the api downstreams.
 */
const responseHandlerFactory = namespace => {
  return response => {
    const { api: apiState, form: formState } = appStore.getState();
    const { value: onboardOption } = formState.onboardOption;
    const isGuest = onboardOption === 'guest';
    const responseData = response?.data?.[namespace];
    const { accessToken } = responseData?.buyer.auth || {};
    const { api: apiActions } = appStore.getActions();
    const fundingOption = responseData.fundingOptions?.[0];
    const plan = fundingOption?.allPlans?.[0];
    const fundingSource = plan?.fundingSources?.[0];
    const fundingInstrument = apiState.isBillingWithoutPurchase
      ? fundingOption
      : fundingSource; // without purchase takes first funding instrument
    if (fundingInstrument) {
      const instrumentId = fundingInstrument.fundingInstrument?.id || '';

      logger.info('founding_source_found_and_stored_in_state', {
        instrumentIdPresent: Boolean(instrumentId),
      });
      apiActions.setInstrumentId(instrumentId);
      apiActions.setFundingSource(fundingInstrument);
    }

    // Save access token in store. Not dropping the login cookie until
    // after we check for errors because if we throw an error we'll drop
    // the cookie there
    if (accessToken) {
      const type = isGuest ? 'guest' : 'signup';
      setAccessToken({ type, value: accessToken });

      const { cartAmount, merchantId } = apiState;
      if (isFunction(window.PAYPAL?.analytics?.instance?.recordClick)) {
        const data = ACCOUNT_CREATION_PAYLOAD({
          buyerId: responseData?.buyer?.userId,
          cartAmount,
          locale: getLocale(),
          merchantId,
        });

        logger.info('account_creation_marketing_log', data);
        try {
          // Wrapping this in a try catch because it was blowing up the
          // when we first tried using it. It's used for marketing and it
          // shouldn't kill the flow if it fails.
          window.PAYPAL.analytics.instance.recordClick({ data });
        } catch (error) {
          const { message, stack: stackTrace } = error;
          logger.warn('paypal_analytics_record_click_error', {
            message,
            stackTrace,
          });
        }
      }

      logger.info('access_token_present', { accountType: type });
    }

    // In the incontext flow update the parent window's access token.
    if (isFunction(window.xprops?.onAuth)) {
      logger.info('update_incontext_with_access_token');
      window.xprops.onAuth({ accessToken });
    }

    return conditionallyDropLoginCookie(responseData);
  };
};

const onAfterOnboardAccountCreationHandler =
  responseHandlerFactory('onboardAccount');
const onAfterPaymentPlanCreationHandler = responseHandlerFactory(
  'createOnboardingPaymentPlan',
);
const onAfterOnboardAccountSuccessHandler = responseContingencyHandlerFactory();
const onAfterPaymentPlanSuccessHandler = responseContingencyHandlerFactory();

export {
  onAfterOnboardAccountCreationHandler,
  onAfterOnboardAccountSuccessHandler,
  onAfterPaymentPlanCreationHandler,
  onAfterPaymentPlanSuccessHandler,
};
